import React, { useState } from 'react';
import VideoUploadButton from '../../../components/ui/VideoUploadButton';
import Icon from '../../../components/AppIcon';

const OverviewSection = ({ data, onChange, errors, songId, talentId, musicals = [] }) => {
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation functions
  const validateMusical = (value) => {
    if (!value || !value?.trim()) {
      return 'Musical is required';
    }
    return null;
  };

  const validateSongTitle = (value) => {
    if (!value || !value?.trim()) {
      return 'Song title is required';
    }
    if (value?.trim()?.length < 2) {
      return 'Song title must be at least 2 characters';
    }
    return null;
  };

  const validateUrl = (value) => {
    if (!value || !value?.trim()) {
      return null; // Optional field
    }
    try {
      const url = new URL(value);
      if (!['http:', 'https:']?.includes(url?.protocol)) {
        return 'URL must start with http:// or https://';
      }
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleInputChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });

    // Real-time validation
    let error = null;
    if (field === 'musical') {
      error = validateMusical(value);
    } else if (field === 'songTitle') {
      error = validateSongTitle(value);
    }

    setFieldErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Validate on blur
    let error = null;
    if (field === 'musical') {
      error = validateMusical(data?.musical);
    } else if (field === 'songTitle') {
      error = validateSongTitle(data?.songTitle);
    }

    setFieldErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleVideoUploadSuccess = (uploadData) => {
    setUploadedVideos(prev => [...prev, uploadData]);
    setUploadSuccess(`Video uploaded successfully: ${uploadData?.fileName}`);
    setUploadError('');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setUploadSuccess('');
    }, 3000);
  };

  const handleVideoUploadError = (error) => {
    setUploadError(error);
    setUploadSuccess('');
  };

  const typeOfSongOptions = [
    { value: '', label: 'Select Type of Song' },
    { value: 'Solo', label: 'Solo' },
    { value: 'Duet', label: 'Duet' },
    { value: 'Group', label: 'Group' }
  ];

  const musicalKeyOptions = [
    { value: '', label: 'Select Musical Key' },
    { value: 'C', label: 'C Major' },
    { value: 'C#', label: 'C# Major' },
    { value: 'Db', label: 'Db Major' },
    { value: 'D', label: 'D Major' },
    { value: 'D#', label: 'D# Major' },
    { value: 'Eb', label: 'Eb Major' },
    { value: 'E', label: 'E Major' },
    { value: 'F', label: 'F Major' },
    { value: 'F#', label: 'F# Major' },
    { value: 'Gb', label: 'Gb Major' },
    { value: 'G', label: 'G Major' },
    { value: 'G#', label: 'G# Major' },
    { value: 'Ab', label: 'Ab Major' },
    { value: 'A', label: 'A Major' },
    { value: 'A#', label: 'A# Major' },
    { value: 'Bb', label: 'Bb Major' },
    { value: 'B', label: 'B Major' },
    { value: 'Am', label: 'A Minor' },
    { value: 'Bm', label: 'B Minor' },
    { value: 'Cm', label: 'C Minor' },
    { value: 'Dm', label: 'D Minor' },
    { value: 'Em', label: 'E Minor' },
    { value: 'Fm', label: 'F Minor' },
    { value: 'Gm', label: 'G Minor' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
      <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
        <Icon name="FileText" size={24} className="text-blue-200" />
        <span>Overview</span>
        <span className="text-xs bg-red-500 bg-opacity-20 text-red-200 px-2 py-1 rounded-full">
          Required: Musical & Song Title
        </span>
      </h3>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Musical Dropdown - Updated to use musicals prop */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Musical <span className="text-red-400">*</span>
            </label>
            <select
              value={data?.musical || ''}
              onChange={(e) => handleInputChange('musical', e?.target?.value)}
              onBlur={() => handleBlur('musical')}
              className={`w-full px-4 py-2 bg-white bg-opacity-20 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                touched?.musical && fieldErrors?.musical
                  ? 'border-red-400 focus:ring-red-400' :'border-white border-opacity-30 focus:ring-blue-400'
              }`}
              required
            >
              <option value="" className="text-gray-800">Select a Musical</option>
              {musicals?.map((musical) => (
                <option key={musical?.id} value={musical?.title} className="text-gray-800">
                  {musical?.title}
                </option>
              ))}
            </select>
            {touched?.musical && fieldErrors?.musical && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="AlertCircle" size={14} className="text-red-300" />
                <p className="text-red-300 text-sm">{fieldErrors?.musical}</p>
              </div>
            )}
            {errors?.musical && (
              <p className="text-red-300 text-sm mt-1">{errors?.musical}</p>
            )}
          </div>

          {/* Song Title - Required */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Song Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={data?.songTitle || ''}
              onChange={(e) => handleInputChange('songTitle', e?.target?.value)}
              onBlur={() => handleBlur('songTitle')}
              className={`w-full px-4 py-2 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 ${
                touched?.songTitle && fieldErrors?.songTitle
                  ? 'border-red-400 focus:ring-red-400' :'border-white border-opacity-30 focus:ring-blue-400'
              }`}
              placeholder="Enter song title"
              required
            />
            {touched?.songTitle && fieldErrors?.songTitle && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="AlertCircle" size={14} className="text-red-300" />
                <p className="text-red-300 text-sm">{fieldErrors?.songTitle}</p>
              </div>
            )}
            {errors?.songTitle && (
              <p className="text-red-300 text-sm mt-1">{errors?.songTitle}</p>
            )}
          </div>
        </div>

        {/* Keep existing optional fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Character */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Character
            </label>
            <input
              type="text"
              value={data?.character || ''}
              onChange={(e) => onChange({ ...data, character: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Character name"
            />
          </div>

          {/* Song Placement in Show */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Song Placement in Show
            </label>
            <input
              type="text"
              value={data?.songPlacementInShow || ''}
              onChange={(e) => onChange({ ...data, songPlacementInShow: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., Act 1, Scene 2"
            />
          </div>

          {/* Emotional Core */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Emotional Core
            </label>
            <input
              type="text"
              value={data?.emotionalCore || ''}
              onChange={(e) => onChange({ ...data, emotionalCore: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Primary emotion"
            />
          </div>

          {/* Objective */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Objective
            </label>
            <input
              type="text"
              value={data?.objective || ''}
              onChange={(e) => onChange({ ...data, objective: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Character's goal"
            />
          </div>

          {/* Vocal Range */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Vocal Range
            </label>
            <input
              type="text"
              value={data?.vocalRange || ''}
              onChange={(e) => onChange({ ...data, vocalRange: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., C4-G5"
            />
          </div>

          {/* Musical Style */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Musical Style
            </label>
            <input
              type="text"
              value={data?.musicalStyle || ''}
              onChange={(e) => onChange({ ...data, musicalStyle: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., Ballad, Up-tempo"
            />
          </div>

          {/* Musical Key */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Musical Key
            </label>
            <input
              type="text"
              value={data?.musicalKey || ''}
              onChange={(e) => onChange({ ...data, musicalKey: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., C Major, F# Minor"
            />
          </div>

          {/* Type of Song */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Type of Song
            </label>
            <input
              type="text"
              value={data?.typeOfSong || ''}
              onChange={(e) => onChange({ ...data, typeOfSong: e?.target?.value })}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., Solo, Duet, Ensemble"
            />
          </div>
        </div>

        {/* Video Upload Section */}
        {songId && (
          <div className="mt-6">
            <VideoUploadButton
              songId={songId}
              talentId={talentId}
              level="overview"
              section="general"
              onUploadSuccess={handleVideoUploadSuccess}
              onUploadError={handleVideoUploadError}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewSection;